/**
 * Full-screen camera scanner. Opened by a tap (a user gesture, which iOS
 * requires), closed by the customer or by a machine code being read.
 *
 * Only a machine QR ends the scan: any other code shows a short notice and the
 * camera keeps reading. The stream is stopped whenever the scanner unmounts.
 */
import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import { isAppEntryQr, machineCodeFrom } from "../qr";
import { createDecoder } from "./decoder";

type Phase = "starting" | "scanning" | "denied" | "unsupported" | "failed";

const PROBLEM_KEY: Record<Exclude<Phase, "starting" | "scanning">, string> = {
  denied: "vend.scan.denied",
  unsupported: "vend.scan.unsupported",
  failed: "vend.scan.failed",
};

interface Props {
  onCode: (code: string) => void;
  onClose: () => void;
  onManual: () => void;
}

export function QrScanner({ onCode, onClose, onManual }: Props) {
  const { t } = useI18n();
  const video = useRef<HTMLVideoElement>(null);
  const track = useRef<MediaStreamTrack | null>(null);
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const [phase, setPhase] = useState<Phase>("starting");
  const [notice, setNotice] = useState<string | null>(null);
  const [torch, setTorch] = useState<"none" | "off" | "on">("none");

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer: number | undefined;
    let lastNotice = 0;

    void (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setPhase("unsupported");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
      } catch (e) {
        const name = e instanceof DOMException ? e.name : "";
        setPhase(name === "NotAllowedError" || name === "SecurityError" ? "denied" : "failed");
        return;
      }
      const el = video.current;
      if (stopped || !el) {
        stream.getTracks().forEach((tr) => tr.stop());
        return;
      }
      el.srcObject = stream;
      await el.play().catch(() => undefined);

      const videoTrack = stream.getVideoTracks()[0] ?? null;
      track.current = videoTrack;
      const caps = videoTrack?.getCapabilities?.() as { torch?: boolean } | undefined;
      if (caps?.torch) setTorch("off");

      const decode = await createDecoder();
      if (stopped) return;
      if (!decode) {
        setPhase("unsupported");
        return;
      }
      setPhase("scanning");

      const tick = async () => {
        if (stopped) return;
        try {
          const text = await decode(el);
          if (text) {
            const code = machineCodeFrom(text);
            if (code) {
              stopped = true;
              onCodeRef.current(code);
              return;
            }
            const now = Date.now();
            if (now - lastNotice > 2500) {
              lastNotice = now;
              // The "open the app" QR is not a machine: say so plainly.
              setNotice(isAppEntryQr(text) ? "vend.scan.appQr" : "vend.scan.notMachine");
            }
          }
        } catch {
          /* an unreadable frame is skipped */
        }
        timer = window.setTimeout(() => requestAnimationFrame(() => void tick()), 200);
      };
      void tick();
    })();

    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
      track.current = null;
    };
  }, []);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 2500);
    return () => window.clearTimeout(id);
  }, [notice]);

  async function toggleTorch() {
    const tr = track.current;
    if (!tr) return;
    const next = torch !== "on";
    try {
      await tr.applyConstraints({ advanced: [{ torch: next } as MediaTrackConstraintSet] });
      setTorch(next ? "on" : "off");
    } catch {
      setTorch("none");
    }
  }

  const problem = phase === "denied" || phase === "unsupported" || phase === "failed";

  return (
    <div className="v-scanner" role="dialog" aria-modal="true" aria-label={t("vend.scan.open")}>
      <div className="v-scanner__bar">
        <button type="button" className="v-scanner__icon" onClick={onClose} aria-label={t("common.close")}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        {torch !== "none" && (
          <button
            type="button"
            className="v-scanner__icon"
            aria-pressed={torch === "on"}
            onClick={() => void toggleTorch()}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 2h6l-1 7h3l-7 13 1-9H8z" />
            </svg>
            <span className="aseed-visually-hidden">{t("vend.scan.torch")}</span>
          </button>
        )}
      </div>

      <div className="v-scanner__view">
        <video ref={video} className="v-scanner__video" playsInline muted autoPlay />
        {!problem && <div className="v-scanner__frame" aria-hidden="true" />}
      </div>

      <div className="v-scanner__foot" aria-live="polite">
        {problem ? (
          <>
            <p className="v-scanner__text">{t(PROBLEM_KEY[phase])}</p>
            <button type="button" className="aseed-button v-wide" onClick={onManual}>
              {t("vend.manual.title")}
            </button>
          </>
        ) : (
          <>
            <p className="v-scanner__text">
              {notice ? t(notice) : t(phase === "starting" ? "vend.scan.preparing" : "vend.scan.aim")}
            </p>
            <button type="button" className="v-link v-link--light" onClick={onManual}>
              {t("vend.manual.title")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
